import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import DebounceInput from "react-debounce-input";
import { HiUser } from "react-icons/hi";

import api from "../services/api";

export default function UserSelector({
  value,
  onChange,
  disabled = false,
  placeholder = "Filter by user…",
  userRoles = ["super_admin", "admin", "user", "client"],
  buttonClassName = "border border-gray-300 rounded-md pl-9 pr-3 py-2",
  width = "w-full",
}) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  const currentUser = useSelector((state) => state.Auth.user);

  // Fetch users from the API
  const searchUser = async (str) => {
    try {
      if (!str) {
        setUsers([currentUser]);
        return;
      }
      const res = await api.post("/user/search", { search: str, role: userRoles, per_page: 30, sort: "name" });
      if (res.ok && res.data && res.data.users) {
        setUsers(res.data.users);
        setShowSuggestions(true);
      } else {
        console.error("Failed to fetch users:", res.error || "Unknown error");
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  useEffect(() => {
    searchUser(search);
  }, [search]);

  // Handle clicks outside the component to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={wrapperRef} className={`relative ${width}`}>
      <div className="relative w-full">
        {value?._id ? (
          <img src={value.avatar} alt={value.name} className="absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full object-cover pointer-events-none" />
        ) : (
          <HiUser className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        )}
        <DebounceInput
          value={search}
          disabled={disabled}
          debounceTimeout={300}
          className={`w-full text-sm text-gray-700 ${buttonClassName} ${value?._id ? "placeholder-gray-700" : ""}`}
          placeholder={value?.name || placeholder}
          onChange={(e) => {
            e.persist();
            setSearch(e.target.value);
          }}
          onFocus={() => setShowSuggestions(true)}
        />
        {value?._id && !disabled && (
          <span
            onClick={() => onChange(null)}
            className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-700 text-sm leading-none"
            title="Clear">
            ×
          </span>
        )}
      </div>
      {showSuggestions && users.length > 0 && (
        <ul className="absolute z-50 bg-white border border-gray-300 w-full max-h-60 overflow-y-auto shadow-lg rounded-md">
          {users.map((user) => (
            <li
              key={user._id}
              className={`flex items-center p-2 hover:bg-gray-100 cursor-pointer ${value && value._id === user._id ? "bg-blue-500 text-white" : ""}`}
              onClick={() => {
                setSearch("");
                onChange(user);
                setShowSuggestions(false);
              }}>
              <img src={user.avatar || "/default-avatar.png"} alt="Avatar" className="w-8 h-8 rounded-full mr-3 object-cover" />
              <div className="flex flex-col">
                <span className="font-semibold">{user.name}</span>
                <span className="text-gray-500 text-sm">{user.contract}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
